"use client";
import React, { useEffect, useState, useMemo } from "react";
import { useCookies } from "react-cookie";
import { useRouter } from "next13-progressbar";
import { PrimaryButton } from "../button/primaryButton";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { DateTime } from "luxon";
import { SuccessBanner, UnavailableBanner } from "./cartStatusBanner";
import { usePathname } from "next/navigation";
import CloseIcon from "@/components/_icons/closeIcon";
import { OrderType } from "@/types";
import { OrderTypeDropdown } from "../orderTypeDropdown/orderTypeDropdown";
import {
  ScheduledDateDropdown,
  ScheduleDateType,
} from "../scheduledDateDropdown/scheduledDateDropdown";
import {
  ScheduledTimeDropdown,
  ScheduleTimeType,
} from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { AgeRestricted } from "../ageRestricted/ageRestricted";
import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";
import { GiftCardCartItem } from "@/app/(shop)/giftcard/components/giftCardCartItem";
import { GiftCardCartOrdersBanner } from "@/app/(shop)/giftcard/components/giftCardCartOrdersBanner";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { DeliveryEstimateBanner } from "@/components/deliveryEstimateBanner/deliveryEstimateBanner";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { RetailCartItem } from "@/dto/retailCartItem";
import { ScheduledTime } from "@/dto/scheduledTime";
import { GiftCard } from "@/dto/giftCard";
import { Address } from "@/dto/address";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { OrderEstimates } from "@/dto/orderEstimates";
import { ServiceCartItem } from "@/dto/serviceCartItem";
import { TrashIcon } from "../_icons/trashIcon";
import { Service } from "@/dto/service";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { DrawerModal } from "../modals/modals";
import { useCurrency } from "@/app/providers/currencyContext";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import ArrowRightIcon from "../_icons/arrowRightIcon";
import { CartMerchantInfo } from "./cartMerchantInfo";
import { useMessages, useTranslations } from "next-intl";
import useMediaQuery from "@/utils/useMediaQuery";
import { getItemUrl } from "@/utils/itemUrlUtils";
import { modifierDisplay } from "@/utils/modifierDisplayUtil";
import { removeHashFromURL } from "@/utils/cartUtils";

const extractHashFromUrl = (url: string) => {
  const urlObj = new URL(url);
  return urlObj.hash;
};

export const CartDrawer = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  retailPickupTime,
  setDeliveryAddressModalOpen,
  setIsCartOpen,
  setDeliveryAddressErrorModalOpen,
}: any) => {
  const hash =
    typeof window !== "undefined"
      ? extractHashFromUrl(window?.location?.href)
      : "";
  const pathName = usePathname();
  const t = useTranslations("CartDrawer");
  const messages = useMessages();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const isHybridMerchant = merchant.isHybrid;
  const merchantService = merchant?.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const deliveryService = merchant?.services.find(
    (s: Service) => s.type === "DELIVERY"
  );
  const now = new Date();
  const [cookies] = useCookies(["session"]);
  const router = useRouter();
  const [cartItems, setCartItems] = useState(() => {
    const storedCart = getFromLocalStorage<RetailCartItem[]>(
      LocalStorageKey.CART,
      []
    );
    return storedCart ? storedCart : [];
  });
  const [orderType, setOrderType] = useState<OrderType>((): OrderType => {
    const sessionTime = getFromSessionStorage<ScheduledTime>(
      SessionStorageKey.SCHEDULED_TIME,
      {} as ScheduledTime
    );
    const savedOrderType = sessionTime.orderType;

    const retailServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = retailServices.filter(
      ({ service }) => service?.enabled
    );

    if (
      savedOrderType &&
      enabledServices.find(
        (s: { type: OrderType; service: Service }) => s.type === savedOrderType
      )
    ) {
      return savedOrderType;
    } else {
      return enabledServices.length > 0
        ? enabledServices[0]?.type
        : OrderType.PICKUP;
    }
  });
  const [showScheduledOrderForm, setShowScheduledOrderForm] =
    useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: "Today",
    dateObject: new Date(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now),
  });
  const [giftCard, setGiftCard] = useState<GiftCard | null>(null);
  const businessHours = merchant?.businessHours?.hours;
  const allDaysEmpty =
    businessHours &&
    Object.values(businessHours).every((day: any) => day.length === 0);
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  let hasItemsInCart = cartItems.length > 0;
  const isMinimumMet = () => {
    const totalPrice =
      cartItems.reduce((total: number, i: any) => (total += i.value.total), 0) /
      100;

    return merchant.deliveryMinimum / 100 <= totalPrice;
  };
  let checkoutDisabled =
    (cartItems.length == 0 && !giftCard) ||
    (!businessOpen && hasItemsInCart) ||
    !businessHours ||
    allDaysEmpty ||
    (retailPickupTime &&
      Object.keys(retailPickupTime).length === 0 &&
      hasItemsInCart) ||
    (orderType === OrderType.DELIVERY && !isMinimumMet());

  const itemsCount =
    cartItems.reduce(
      (adder: any, item: any) => adder + item.value?.quantity,
      0
    ) + (giftCard ? 1 : 0);

  const { format } = useCurrency();

  useEffect(() => {
    if (merchant.isRetail) {
      saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
        orderType: orderType,
      });
    }
    const currentAddress = getFromLocalStorage<Address>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {} as Address
    );
    const fetchEstimate = async () => {
      if (orderType === OrderType.DELIVERY) {
        if (!currentAddress?.address1) {
          setDeliveryAddressModalOpen(true);
          setIsCartOpen(false);
        } else {
          try {
            const result = await getOrderEstimate(
              merchant.id,
              currentAddress,
              merchant.retail
                ? retailPickupTime?.pickupDateTimeMillis
                : undefined
            );
            saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, result);
          } catch {
            setDeliveryAddressModalOpen(false);
            setDeliveryAddressErrorModalOpen(true);
            onRequestClose();
          }
        }
      }
    };
    fetchEstimate();
  }, [orderType]);

  useEffect(() => {
    if (isOpen && businessHours) {
      const pickupTimeData = {
        epoch: retailPickupTime?.pickupDateTimeMillis,
        type: "scheduled",
      };
      saveToSessionStorage(SessionStorageKey.PICKUP_TIME, pickupTimeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, businessHours, merchantMeta?.leadTime, merchantMeta?.timezone]);
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (isMobileScreen) return;
      if (event.key === "cart") {
        const storedCart = getFromLocalStorage<ServiceCartItem[] | null>(
          LocalStorageKey.CART,
          null
        );
        const storedGiftCard = getFromLocalStorage<GiftCard | null>(
          LocalStorageKey.GIFT_CARD,
          null
        );
        if (storedCart) {
          setCartItems(storedCart);
        } else {
          setCartItems([]);
        }
        if ((storedCart && storedCart?.length > 0) || storedGiftCard) {
          setIsCartOpen(true);
        }
      } else if (event.key === "delivery-order-type") {
        setOrderType(OrderType.DELIVERY);
      } else if (event.key === "pickup-order-type") {
        const retailServices = [
          { type: OrderType.PICKUP, service: merchantService },
          { type: OrderType.DELIVERY, service: deliveryService },
        ];
        const enabledServices = retailServices.filter(
          ({ service }) => service?.enabled
        );
        if (enabledServices.length >= 1) {
          setOrderType(enabledServices[0]?.type as OrderType);
        } else {
          setOrderType(OrderType.PICKUP);
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileScreen]);

  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      const storedCart = getFromLocalStorage<ServiceCartItem[]>(
        LocalStorageKey.CART,
        []
      );
      if (storedCart) {
        setCartItems(storedCart);
      }
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const removeFromCart = (event: React.MouseEvent, index: any) => {
    event.stopPropagation();
    setCartItems((prevCartItems: any[]) =>
      prevCartItems.filter((_: any, i: any) => i !== index)
    );
  };

  useEffect(() => {
    saveToLocalStorage(LocalStorageKey.CART, cartItems);
  }, [cartItems]);

  useEffect(() => {
    const storedGiftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );
    setGiftCard(storedGiftCard);
  }, [isOpen]);

  const handleBackToCart = () => {
    setShowScheduledOrderForm(false);
  };

  const handleConfirmScheduledOrder = () => {
    saveToSessionStorage<ScheduledTime>(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: scheduleDate,
      time: scheduleTime,
    });
    setShowScheduledOrderForm(false);
  };

  const handleContinueToCheckout = () => {
    const address = getFromLocalStorage<Address>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {} as Address
    );
    if (orderType === OrderType.DELIVERY && !address?.address1) {
      setDeliveryAddressModalOpen(true);
      setIsCartOpen(false);
    } else {
      if (cookies.session) {
        router.push("/checkout");
      } else {
        onTriggerLoginModal(true);
      }
    }
  };

  const handleCartItemClick = (item: RetailCartItem) => {
    const selectedItem = item.value;
    if (merchant.isHybrid) {
      router.push(`/shop/${getItemUrl(selectedItem)}`);
    } else if (merchant.isRetail) {
      router.push(`/${getItemUrl(selectedItem)}`);
    }
    onRequestClose();
  };

  const pickupTimeDisplay =
    orderType === OrderType.DELIVERY
      ? formatDeliveryTime(merchant?.timezone, merchant?.locale, messages)
      : formatPickupTime(
          retailPickupTime?.pickupDateTimeMillis,
          merchant.timezone,
          merchant.locale,
          messages
        );

  return (
    <DrawerModal
      isOpen={isOpen}
      onRequestClose={() => {
        setShowScheduledOrderForm(false);
        removeHashFromURL(hash, pathName!);
        onRequestClose();
      }}
    >
      <div className="flex flex-col h-full w-[100%]" data-testid="retail-cart">
        {showScheduledOrderForm ? (
          <div className="flex flex-col h-full p-4">
            <div className="pt-4 flex flex-row gap-2">
              <button
                onClick={() => {
                  handleBackToCart();
                }}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon />
              </button>
              <h2 className="text-xl font-bold">{t("scheduleYourOrder")}</h2>
            </div>
            <div className="py-4">
              <ScheduledDateDropdown
                scheduleDate={scheduleDate}
                setScheduleDate={setScheduleDate}
                scheduleDateRange={3}
                setScheduleTime={setScheduleTime}
                businessHours={businessHours}
                timezone={merchant.timezone}
              />
            </div>
            {businessHours && !allDaysEmpty && (
              <div className="py-4">
                <ScheduledTimeDropdown
                  scheduleTime={scheduleTime}
                  setScheduleTime={setScheduleTime}
                  businessHours={businessHours}
                  scheduleDate={scheduleDate}
                  leadTime={merchant.leadTime}
                  timezone={merchant.timezone}
                  orderType={orderType}
                />
              </div>
            )}
            <div className="mt-auto pb-4 flex gap-4">
              <button
                className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
                onClick={handleConfirmScheduledOrder}
                disabled={scheduleTime.displayValue == ""}
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-start px-4 pt-4 sm:pt-8 pb-[13px] sm:pb-0 shadow-cartHeader sm:shadow-none">
              <div>
                <h2 className="text-xl font-medium sm:text-2xl sm:font-bold">
                  {isHybridMerchant && !cartItems.length && !giftCard
                    ? t("appointmentSummary")
                    : t("yourCart", { count: itemsCount })}
                </h2>
              </div>

              <button
                onClick={() => {
                  removeHashFromURL(hash, pathName!);
                  onClose();
                }}
                className="text-gray-600 hover:text-gray-900 mr-4"
              >
                <CloseIcon />
              </button>
            </div>
            <CartMerchantInfo className="sm:hidden" merchant={merchant} />
            <div className="px-4 pb-2 sm:pb-4 flex flex-row justify-between items-center gap-4 pt-6 sm:pt-8">
              <OrderTypeDropdown
                orderType={orderType}
                setOrderType={setOrderType}
                availableOrderTypes={merchant.services}
              />
              {businessHours && !allDaysEmpty && (
                <div className="w-[70%] flex flex-col pb-[22px]">
                  <p className="text-[12px] text-[#727272] pb-2">
                    {t("forWhen")}
                  </p>
                  {retailPickupTime && <>{pickupTimeDisplay}</>}
                </div>
              )}
            </div>
            {hash && hash === "#cart" && (
              <SuccessBanner count={cartItems.length} />
            )}
            {hash && hash === "#cart-unavailable" && (
              <UnavailableBanner count={cartItems.length} />
            )}
            {!businessOpen && hasItemsInCart && giftCard && (
              <div className="mx-4">
                <GiftCardCartOrdersBanner />
              </div>
            )}

            <div className="flex-grow overflow-y-auto p-4">
              {orderType === OrderType.DELIVERY && hasItemsInCart && (
                <DeliveryEstimateBanner
                  merchant={merchant}
                  onAddMore={setIsCartOpen}
                />
              )}
              {cartItems.map((item: any, index: any) => (
                <div
                  key={index}
                  className="flex flex-col justify-between items-center py-4 border-b cursor-pointer"
                  onClick={() => handleCartItemClick(item)}
                >
                  <div className="w-full flex flex-row justify-between">
                    <div className="w-full flex flex-col justify-between">
                      <p className="text-lg text-[#373737] max-w-[310px]">
                        {item.value?.name}{" "}
                        {item.value?.quantity > 1 &&
                          `× ` + item.value?.quantity}
                      </p>
                      {item.value?.isAgeRestricted && (
                        <AgeRestricted text={t("idRequired")} />
                      )}
                      <div className="max-w-[80%]">
                        {item.value?.modifiers?.length > 0 && (
                          <p className="text-[15px] text-[#727272]">
                            {modifierDisplay(item.value?.modifiers)}
                          </p>
                        )}
                        <div className="w-full">
                          <p className="text-[15px] text-[#727272] max-w-[310px] break-words">
                            {item.value?.specialInstructions}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-end">
                      <p className="text-[16px] font-medium mt-0.5">
                        {format(item.value?.total)}
                      </p>
                      <button
                        onClick={(event: React.MouseEvent) =>
                          removeFromCart(event, index)
                        }
                        className="mt-2"
                      >
                        <TrashIcon width={14} height={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {giftCard && (
                <GiftCardCartItem
                  giftCard={giftCard}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    removeFromLocalStorage(LocalStorageKey.GIFT_CARD);
                    setGiftCard(null);
                  }}
                  onRequestClose={onRequestClose}
                  showBottomBorder={true}
                />
              )}
            </div>
            <div className="sm:p-4 sm:pb-8">
              <PrimaryButton
                style={{
                  backgroundColor: checkoutDisabled ? "#C7C7C7" : merchantColor,
                }}
                className="bg-black px-[18px] sm:px-0 text-white font-bold py-2 rounded-none sm:rounded-md w-full h-16 sm:h-14 disabled:bg-[#C7C7C7] flex justify-between sm:justify-center items-center"
                onClick={handleContinueToCheckout}
                disabled={checkoutDisabled}
              >
                <p className="text-xl font-medium">{t("continueToCheckout")}</p>
                <ArrowRightIcon
                  className="sm:hidden"
                  color="white"
                  width={24}
                  height={24.7}
                />
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </DrawerModal>
  );
};

const formatPickupTime = (
  pickupTimeData: number,
  timezone: string,
  locale: string,
  messages: Record<string, any>
) => {
  if (pickupTimeData) {
    const date = new Date(pickupTimeData);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timezone,
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    });
    return `${formattedDate}${
      messages?.CartDrawer?.commaAt
    } ${formattedTime} ${getTimezoneAbbreviation(timezone, locale)}`;
  }

  return messages.CartDrawer.scheduledTimeNotAvailable;
};

const formatDeliveryTime = (
  timezone: string,
  locale: string,
  messages: Record<string, any>
) => {
  const { deliveryTime, pickupTime: deliveryPickupTime } =
    getFromSessionStorage<Partial<OrderEstimates>>(
      SessionStorageKey.ORDER_ESTIMATES,
      {}
    );

  const deliveryPickupTimeDateText = DateTime.fromMillis(
    deliveryPickupTime || 0
  )
    .setZone(timezone)
    .toLocaleString(DateTime.TIME_SIMPLE);

  const deliveryTimeDateText = DateTime.fromMillis(deliveryTime || 0)
    .setZone(timezone)
    .toLocaleString(DateTime.TIME_SIMPLE);
  const timeZoneAbbreviation = getTimezoneAbbreviation(timezone, locale);

  const dt = DateTime.fromMillis(deliveryTime || 0).setZone(timezone);
  const now = DateTime.now().setZone(timezone);

  let dayLabel;
  if (dt.hasSame(now, "day")) {
    dayLabel = messages.CartDrawer.today;
  } else if (dt.hasSame(now.plus({ days: 1 }), "day")) {
    dayLabel = `${messages.CartDrawer.tomorrow} (${dt.toFormat("MMM d")})`;
  } else {
    dayLabel = dt.toFormat("MMM d");
  }

  const deliveryTimeText = [
    messages.CartDrawer.for,
    dayLabel,
    messages.CartDrawer.from,
    deliveryPickupTimeDateText,
    messages.CartDrawer.to,
    deliveryTimeDateText,
    timeZoneAbbreviation,
  ].join(" ");

  return deliveryTimeText;
};
